import React, { useState } from 'react';
import { moduleClientFactory } from '../../../../api-service/module-client-factory';
import { ClassroomDto, EducatorDto } from '../../../../api-service/clients/auto-generated-clients';
import { CourseBulkEditDialogComponent } from './course-bulk-edit-dialog-component';
import { CourseBulkEditResultDialogComponent } from './course-bulk-edit-result-dialog-component';
import { IBulkEditResultSection, lessonResultSection } from './bulk-edit-results';
import { useCompanyEducators } from './use-company-educators';
import { useCompanyClassrooms } from './use-company-classrooms';

export interface IChangeLessonsDialogProps {
  lessonIds: string[];
  companyId: string;
  onCompleted?: () => void;
}

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function ChangeLessonsDialog({ lessonIds, companyId, onCompleted }: IChangeLessonsDialogProps) {
  const { educators, isLoadingEducators } = useCompanyEducators(companyId);
  const { classrooms, isLoadingClassrooms } = useCompanyClassrooms(companyId);
  const [isOpen, setIsOpen] = useState(true);
  const [educatorIdsToAdd, setEducatorIdsToAdd] = useState<string[]>([]);
  const [educatorIdsToRemove, setEducatorIdsToRemove] = useState<string[]>([]);
  const [classroomId, setClassroomId] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);
  const [completedSections, setCompletedSections] = useState<IBulkEditResultSection[]>([]);
  const [hasSkippedItems, setHasSkippedItems] = useState(false);

  function handleEducatorsToAddChange(chosen: EducatorDto[]) {
    setEducatorIdsToAdd(chosen.map(educator => educator.educatorId));
  }

  function handleEducatorsToRemoveChange(chosen: EducatorDto[]) {
    setEducatorIdsToRemove(chosen.map(educator => educator.educatorId));
  }

  function handleClassroomChange(classroom?: ClassroomDto) {
    setClassroomId(classroom?.classroomId);
  }

  async function handleConfirm() {
    setIsSaving(true);
    const sections = [...completedSections];
    try {
      if (educatorIdsToAdd.length > 0 || educatorIdsToRemove.length > 0) {
        const response = await attendanceClient.addAndRemoveLessonEducators({ lessonIds, educatorIdsToAdd, educatorIdsToRemove });
        sections.push(lessonResultSection('educators', response));
        // A rejected classroom change keeps the dialog open, and a retry must not apply the educators twice
        setEducatorIdsToAdd([]);
        setEducatorIdsToRemove([]);
      }
      if (classroomId) {
        const response = await attendanceClient.changeLessonClassroom({ lessonIds, classroomId });
        sections.push(lessonResultSection('classroom', response));
      }
      if (sections.some(section => section.skippedItems.length > 0)) {
        setHasSkippedItems(true);
      } else {
        handleResultClose();
      }
    } catch {
      // The error message interceptor shows the reason, and the dialog stays open so the choice can be corrected
    } finally {
      setCompletedSections(sections);
      setIsSaving(false);
    }
  }

  function handleClose() {
    setIsOpen(false);
    if (completedSections.length > 0) {
      onCompleted?.();
    }
  }

  function handleResultClose() {
    setIsOpen(false);
    onCompleted?.();
  }

  if (!isOpen) {
    return null;
  }

  if (hasSkippedItems) {
    return <CourseBulkEditResultDialogComponent title="Nogle lektioner blev ikke rettet" sections={completedSections} onClose={handleResultClose} />;
  }

  return (
    <CourseBulkEditDialogComponent
      title={lessonIds.length === 1 ? 'Ret undervisere og lokale på den valgte lektion' : `Ret undervisere og lokale på de ${lessonIds.length} valgte lektioner`}
      description="Lektionerne beholder deres undervisere. De valgte undervisere tilføjes eller fjernes. Aflyste lektioner og lektioner med låst protokol ændres ikke."
      educators={educators}
      educatorIdsToAdd={educatorIdsToAdd}
      educatorIdsToRemove={educatorIdsToRemove}
      classrooms={classrooms}
      classroomId={classroomId}
      isLoading={isLoadingEducators || isLoadingClassrooms}
      isSaving={isSaving}
      onEducatorsToAddChange={handleEducatorsToAddChange}
      onEducatorsToRemoveChange={handleEducatorsToRemoveChange}
      onClassroomChange={handleClassroomChange}
      onConfirm={handleConfirm}
      onClose={handleClose}
    />
  );
}
